import { useCallback, useState } from 'react';

import { startingPan, type CropPan } from './photo-crop';
import { usePhotoFeedback } from './use-photo-feedback';
import { usePhotoSelection } from './use-photo-selection';
import { usePhotoTransfer, type PhotoStep, type VenueCacheActions } from './use-photo-transfer';

export type { PhotoFocusTarget, PhotoProblem } from './use-photo-feedback';
export type { VenueCacheActions } from './use-photo-transfer';
export type PhotoPhase = PhotoStep | 'reading';

/**
 * The steps of changing a venue's booking photo: choose a file, frame it, upload it, or remove
 * the uploaded one. It lives above the tabs so an open crop or running upload survives a tab switch.
 */
export function useVenuePhotoFlow(venueId: string, cache: VenueCacheActions) {
  const [step, setStep] = useState<PhotoStep>('idle');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<CropPan>({ x: 0.5, y: 0.5 });
  const feedback = usePhotoFeedback();
  const { focusOn, dismissProblem } = feedback;
  const { selection, reading, choose: read, clear } = usePhotoSelection(feedback.report);

  const backToActions = useCallback(() => {
    setStep('idle');
    focusOn('actions');
  }, [focusOn]);

  const transfer = usePhotoTransfer({
    venueId,
    cache,
    feedback,
    selection,
    zoom,
    pan,
    setStep,
    onDone: () => {
      clear();
      backToActions();
    },
  });

  const choose = async (file: File) => {
    dismissProblem();
    const accepted = await read(file);
    if (!accepted) return;
    setZoom(1);
    setPan(startingPan(accepted.size));
    setStep('cropping');
    focusOn('crop');
  };

  return {
    phase: (reading ? 'reading' : step) satisfies PhotoPhase,
    selection,
    zoom,
    pan,
    progress: transfer.progress,
    problem: feedback.problem,
    announcement: feedback.announcement,
    focusTarget: feedback.focusTarget,
    setZoom,
    setPan,
    choose,
    save: transfer.save,
    remove: transfer.remove,
    cancelUpload: transfer.cancelUpload,
    cancelCrop: () => {
      clear();
      backToActions();
    },
    askToRemove: () => {
      dismissProblem();
      setStep('confirming');
      focusOn('keep');
    },
    keepPhoto: backToActions,
    dismissProblem,
    focusDone: feedback.focusDone,
  };
}

export type VenuePhotoFlow = ReturnType<typeof useVenuePhotoFlow>;
